import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { TvButton, useBackHandler, useFocusScope } from '../tv';

export interface HeaderMenuProps {
  /** Buttons. Clicking any of them closes the menu. */
  children: ReactNode;
  label?: string;
}

/**
 * The header's overflow.
 *
 * Exists for the phone. A 320px header cannot hold a pet's name, its age and
 * two full-width buttons without one of them wrapping onto a second line, and
 * the one that was wrapping — Release — deletes the pet. Putting a destructive
 * action a thumb's width from the title on a device operated by thumbs is the
 * kind of thing that is only ever noticed once.
 *
 * The confirmation dialog is still what actually guards the deletion; this just
 * stops the button being somewhere a thumb lands by accident.
 */
export function HeaderMenu({ children, label = 'More actions' }: HeaderMenuProps) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);

  // The same three ways out a dialog has: Back on a remote, Escape on a
  // keyboard, a tap anywhere else on a touchscreen.
  const panelRef = useFocusScope<HTMLDivElement>(open);
  useBackHandler(close, open);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  return (
    <div className="menu">
      <TvButton
        className="menu__toggle"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        data-testid="header-menu"
        onClick={() => setOpen((current) => !current)}
      >
        {/* Three dots as a glyph rather than an icon font or an SVG: it is the
            one symbol that needs no explaining and no extra bytes. */}
        <span aria-hidden="true">⋯</span>
      </TvButton>

      {open && (
        <>
          {/* Catches the tap that dismisses the menu. Below the panel, above
              everything else — including the canvas, which would otherwise walk
              the pet on the way past. */}
          <div className="menu__scrim" role="presentation" onPointerDown={close} />
          <div
            ref={panelRef}
            className="menu__panel"
            role="menu"
            data-testid="header-menu-panel"
            // Capture rather than per-child wiring: every control in here is a
            // one-shot action, so any of them landing means the menu is done.
            onClick={close}
          >
            {children}
          </div>
        </>
      )}
    </div>
  );
}
